<template>
    <BulkActionBar
        :show-bulk-select="false"
        :items="items"
        :selected-list-ids="selectedListIds"
        :is-mobile="$vuetify.breakpoint.smAndDown"
        :items-text="$t('invoice table row')"
        :items-errors-text="$t('invoice table row(s) had errors')"
        :items-selected-text="$t('invoice table row(s) selected')"
        :relative-elem="relativeElem"
        @bulkSelection="showSelectionCheckbox = $event"
    >
        <template v-slot:actions>
            <v-btn
                v-if="canCreateInvoice"
                text
                color="white"
                @click="onCreateInvoicesClick"
            >
                <v-icon class="white--text mr-2">mdi-plus-circle</v-icon>
                {{ $t("Create") }}
            </v-btn>
            <v-btn
                v-if="canMarkPaid"
                text
                color="white"
                @click="onMarkPaidClick"
            >
                <v-icon class="white--text mr-2">mdi-checkbox-blank-circle-outline</v-icon>
                {{ $t("Mark paid") }}
            </v-btn>
            <v-btn
                v-if="canSendInvoices"
                text
                color="white"
                @click="onSendInvoicesClick"
            >
                <v-icon class="white--text mr-2">mdi-send</v-icon>
                {{ $t("Send to client") }}
            </v-btn>
            <v-btn
                v-if="canRecalculateInvoice"
                text
                color="white"
                :loading="calculateLoading"
                @click="onCalculateSalesInvoices"
            >
                <v-icon class="white--text mr-2">mdi-calculator</v-icon>
                {{ $t("Recalculate") }}
            </v-btn>
            <v-menu
                v-if="selectedAccount && selectedAccount.type === 'company'"
                content-class="overflow-y-hidden dropmenu"
                bottom
                offset-y
                left
            >
                <template v-slot:activator="{ on }">
                    <v-btn
                        class="ml-1"
                        color="white"
                        small
                        icon
                        v-on="on"
                    >
                        <v-icon>more_vert</v-icon>
                    </v-btn>
                </template>
                <v-list dense>
                    <v-list-item
                        v-if="canExportInvoice && isAdminOrOwner"
                        class="menu-list-item"
                        style="height: 32px !important; min-height: 32px !important"
                        @click="onExportInvoicesClick()"
                    >
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ $t("Export to {0}").format(selectedVendorName) }}
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                    <v-list-item
                        v-if="canUpdateInvoiceStatus"
                        class="menu-list-item"
                        style="height: 32px !important; min-height: 32px !important"
                        @click="onUpdateInvoicesStatusClick()"
                    >
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ $t("Update Status from {0}").format(selectedVendorName) }}
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                    <v-list-item
                        v-if="canVoidInvoice && isAdminOrOwner"
                        class="menu-list-item"
                        style="height: 32px !important; min-height: 32px !important"
                        @click="onVoidInvoicesClick()"
                    >
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ $t("Void invoice") }}
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                    <v-list-item
                        v-if="canDeleteInvoicesClick && isAdminOrOwner"
                        class="menu-list-item"
                        style="height: 32px !important; min-height: 32px !important"
                        @click="onDeleteInvoicesClick()"
                    >
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ $t("Delete invoice") }}
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                    <v-list-item
                        v-if="canRestoreInvoice && isAdminOrOwner"
                        class="menu-list-item"
                        style="height: 32px !important; min-height: 32px !important"
                        @click="onRestoreInvoicesClick()"
                    >
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ $t("Restore invoice") }}
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item
                        v-if="canMarkUnpaid && isAdminOrOwner"
                        class="menu-list-item"
                        style="height: 32px !important; min-height: 32px !important"
                        @click="onMarkUnpaidClick"
                    >
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ $t("Mark unpaid") }}
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item
                        class="menu-list-item"
                        style="height: 32px !important; min-height: 32px !important"
                        @click="onInvoiceExportExcel()"
                    >
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ $t("Invoices") + " - " + $t("Download to Excel") }}
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item
                        v-if="canExportRevenueSplit"
                        class="menu-list-item"
                        style="height: 32px !important; min-height: 32px !important"
                        @click="onRevenueSplitExportExcel()"
                    >
                        <v-list-item-content>
                            <v-list-item-title>
                                {{ $t("Revenue split") + " - " + $t("Download to Excel") }}
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>

                </v-list>
            </v-menu>
        </template>
    </BulkActionBar>
</template>

<script>
import {ref, computed} from "@vue/composition-api";
import BulkActionBar from "../BulkActionBar";
import {$t} from "@/i18n";
import DialogModule, {actions as dialogActions} from "../../store/modules/dialog";
import AppModule from "../../store/modules/app";
import {get} from "@/helpers/get-set";
import RightsModule from "../../composables/use-rights";
import CreateInvoicesDialog from "../../views/Invoice/CreateInvoicesDialog";
import SendInvoicesDialog from "../../views/Invoice/SendInvoicesDialog";
import DeleteInvoicesDialog from "../../views/Invoice/DeleteInvoicesDialog";
import ExportInvoicesDialog from "../../views/Invoice/ExportInvoicesDialog";
import SalesInvoiceModule from "../../store/modules/sales_invoice";
import AccountModule from "@/store/modules/account";
import {convertParamsObjectToQueryString} from "@/components/NewFilterBar/FilterBar";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import {APP_STORE_APP_TYPES} from "@/helpers/constants";
import ApiInvoice from "@/api/billing/invoice"
import {getRegionAppTitle} from "@/helpers/helpers";

export default {
    name: "BulkActionBarBilling",
    components: {
        BulkActionBar
    },
    props: {
        items: {
            type: Array,
            default: null
        },
        isMobile: {
            type: Boolean,
            default: false
        },
        selectedListIds: {
            type: Array,
            default: () => []
        },
        showBulkSelect: {
            type: Boolean,
            default: true
        },
        selectedAccount: {
            type: Object,
            default: null
        },
        relativeElem: {
            type: Object,
            default: null
        },
        statuses: {
            type: Array,
            default: () => []
        },
        selectedStatusIndex: {
            type: Number,
            default: null
        },
        selectedInvoices: {
            type: Array,
            default: () => []
        },
        updateTotals: {
            type: Function,
            default: null
        },
        vendorType: {
            type: String,
            default: null
        }
    },
    setup(props, ctx) {
        const PROTOCOL = process.env.VUE_APP_PROTOCOL ? process.env.VUE_APP_PROTOCOL : "";
        const HOST = process.env.VUE_APP_HOST ? process.env.VUE_APP_HOST : "";
        const BASE_URL = `${PROTOCOL}://${HOST}`;

        const {is_admin_or_owner, is_client_admin, can_send_invoices_to_clients} = RightsModule();
        const {
            calculateSalesInvoices,
            cancelSalesInvoices,
            restoreSalesInvoices,
            markPaidInvoices,
            markUnpaidInvoices,
            getUpdatableStatusInvoices
        } = SalesInvoiceModule();
        const {available_company_accounts} = AccountModule();
        const calculateLoading = ref(false);

        const showBulkActions = computed(() => {
            return is_admin_or_owner(props.selectedAccount)
        });

        const isAdminOrOwner = computed(() => is_admin_or_owner(props.selectedAccount))
        const isClientAdmin = computed(() => {
            for (const invoice of props.selectedInvoices) {
                if (!is_client_admin(invoice.client_id)) {
                    return false
                }
            }
            return true
        })

        const canExportRevenueSplit = computed(() => {
            return is_admin_or_owner(props.selectedAccount) && [2, 3, 4].includes(props.selectedStatusIndex)
        });

        const canRecalculateInvoice = computed(() => {
            let s = props.statuses[props.selectedStatusIndex].name;
            return (s === "in_progress" || s === "calculated" || s === "") && isAdminOrOwner.value
        })

        const canCreateInvoice = computed(() => {
            let s = props.statuses[props.selectedStatusIndex].name;
            return (s === "in_progress" || s === "calculated" || s === "") && (isAdminOrOwner.value || isClientAdmin.value)
        });

        const realSelectedInvoices = computed(() => {
            return props.items.filter(i => props.selectedInvoices.some(c => c.id === i.id))
        });

        const {onCreateInvoicesClick} = CreateInvoicesDialog(0, realSelectedInvoices, props.updateTotals);
        const {onSendInvoicesClick} = SendInvoicesDialog(0, realSelectedInvoices, props.updateTotals);
        const {onDeleteInvoicesClick} = DeleteInvoicesDialog(0, realSelectedInvoices, props.updateTotals);
        const {onExportInvoicesClick} = ExportInvoicesDialog(0, realSelectedInvoices, props.updateTotals);

        const canDownloadExcel = computed(() => isAdminOrOwner.value)

        const canExportInvoice = computed(() => {
            if (!props.vendorType) return false
            let s = props.statuses[props.selectedStatusIndex].name;
            return (s === "created" || s === "sent" || s === "exported" || s === "paid" || s === "");
        });
        const canSendInvoices = computed(() => {
            if (!can_send_invoices_to_clients(props.selectedAccount)) {
                return false
            }

            let s = props.statuses[props.selectedStatusIndex].name;
            return (s === "created" || s === "sent" || s === "exported" || s === "paid" || s === "") && isAdminOrOwner.value;
        });
        const canVoidInvoice = computed(() => {
            let s = props.statuses[props.selectedStatusIndex].name;
            return (s !== "sent" && s !== "paid" && s !== "exported" && s !== "annulled") && isAdminOrOwner.value;
        });
        const canRestoreInvoice = computed(() => {
            let s = props.statuses[props.selectedStatusIndex].name;
            return (s === "annulled") && isAdminOrOwner.value;
        });
        const canMarkPaid = computed(() => {
            let s = props.statuses[props.selectedStatusIndex].name;
            return ["created", "sent", ""].includes(s);
        })
        const canMarkUnpaid = computed(() => {
            let s = props.statuses[props.selectedStatusIndex].name;
            return ["paid", ""].includes(s);
        })
        const canUpdateInvoiceStatus = computed(() => {
            if (!props.vendorType) return false
            return getUpdatableStatusInvoices(realSelectedInvoices.value, props.vendorType).length
        })

        const onCalculateSalesInvoices = function () {
            calculateLoading.value = true;
            calculateSalesInvoices(props.selectedAccount.id, realSelectedInvoices.value.map(x => x.id), 'calculated')
                .then((invoices) => {
                    calculateLoading.value = false;
                    props.updateTotals();
                })
                .catch(ex => {
                    calculateLoading.value = false;
                    errorDialog.showError(ex);
                });
        };

        const onVoidInvoicesClick = () => {
            cancelSalesInvoices(props.selectedAccount.id, realSelectedInvoices.value.map(x => x.id))
                .then((invoices) => {
                    let newInvoices = props.selectedInvoices.filter(x => !invoices.find(z => z.id === x.id))
                    ctx.emit("selectedInvoicesUpdated", newInvoices)
                    props.updateTotals();
                })
                .catch(ex => errorDialog.showError(ex));
        };

        const onRestoreInvoicesClick = () => {
            restoreSalesInvoices(props.selectedAccount.id, realSelectedInvoices.value.map(x => x.id))
                .then((invoices) => {
                    let newInvoices = props.selectedInvoices.filter(x => !invoices.find(z => z.id === x.id))
                    ctx.emit("selectedInvoicesUpdated", newInvoices)
                    props.updateTotals();
                })
                .catch(ex => errorDialog.showError(ex));
        };

        const canDeleteInvoicesClick = computed(() => {
            return realSelectedInvoices.value.every(invoice => invoice.status === 'annulled')
        })

        const onMarkPaidClick = function () {
            markPaidInvoices(props.selectedAccount.id, realSelectedInvoices.value.map(x => x.id))
                .then((invoices) => {
                    let newInvoices = props.selectedInvoices.filter(x => !invoices.find(z => z.id === x.id))
                    ctx.emit("selectedInvoicesUpdated", newInvoices)

                    props.updateTotals();
                })
                .catch(ex => errorDialog.showError(ex));
        }

        const onMarkUnpaidClick = function () {
            markUnpaidInvoices(props.selectedAccount.id, realSelectedInvoices.value.map(x => x.id))
                .then((invoices) => {
                    let newInvoices = props.selectedInvoices.filter(x => !invoices.find(z => z.id === x.id))
                    ctx.emit("selectedInvoicesUpdated", newInvoices)

                    props.updateTotals();
                })
                .catch(ex => errorDialog.showError(ex));
        }

        const onInvoiceExportExcel = () => {
            const invoiceIdList = realSelectedInvoices.value.map(x => x.id);
            const string_params = convertParamsObjectToQueryString({"invoice_id_list": invoiceIdList})
            window.open(BASE_URL + "/api/sales_invoices/" + props.selectedAccount.id + "/export/lines/excel" + string_params, "_top");
        }

        const onRevenueSplitExportExcel = () => {
            const invoiceIdList = realSelectedInvoices.value.map(x => x.id);
            const string_params = convertParamsObjectToQueryString({"invoice_id_list": invoiceIdList})
            window.open(BASE_URL + "/api/sales_invoices/" + props.selectedAccount.id + "/export/revenue_split/excel" + string_params, "_top");
        }

        const onUpdateInvoicesStatusClick = async () => {
            try {
                await ApiInvoice.updateInvoiceStatuses({
                    integration_vendor_name: props.vendorType,
                    account_id: props.selectedAccount.id,
                    invoice_id_list: getUpdatableStatusInvoices(props.selectedInvoices, props.vendorType).map(x => x.id)
                })

                ctx.emit("selectedInvoicesUpdated", [])
                props.updateTotals();
            } catch (e) {
                errorDialog.showError(e)
            }
        }

        const selectedVendorName = computed(() => {
            if (props.selectedAccount && props.vendorType) {
                return getRegionAppTitle(props.vendorType, props.selectedAccount)
            }

            return ''
        })

        return {
            is_admin_or_owner,
            showBulkActions,
            canCreateInvoice,
            onCreateInvoicesClick,
            onSendInvoicesClick,
            onDeleteInvoicesClick,
            onExportInvoicesClick,
            canExportInvoice,
            canSendInvoices,
            canVoidInvoice,
            canRestoreInvoice,
            canRecalculateInvoice,
            isAdminOrOwner,
            onCalculateSalesInvoices,
            calculateLoading,
            onVoidInvoicesClick,
            onRestoreInvoicesClick,
            realSelectedInvoices,
            onInvoiceExportExcel,
            onRevenueSplitExportExcel,
            canMarkPaid,
            canMarkUnpaid,
            onMarkPaidClick,
            onMarkUnpaidClick,
            canDeleteInvoicesClick,
            available_company_accounts,
            canUpdateInvoiceStatus,
            onUpdateInvoicesStatusClick,
            selectedVendorName,
            canExportRevenueSplit,
        }
    }
}

</script>